import { workspacesApi } from "@/lib/api";
import {
  PROFILE_MAGIC_TAG_KEYS,
  PROFILE_MAGIC_TAG_LEGACY_ALIASES,
  readCanonicalTagValue,
} from "@/lib/profileMagicTagKeys";
import type { ProfileMagicTagRow } from "@/lib/profileMagicReadiness";
import {
  mergeRegularCustomerEntry,
  parseRegularCustomerAliases,
  type RegularCustomerAliasEntry,
} from "@/lib/regularCustomerAliases";
import { buildShopDocumentTagPatch } from "@/lib/profileMagicReadiness";
import { unwrapWorkspaceTags } from "@/lib/workspaceAvatarTags";

export async function loadProfileMagicTags(
  workspaceId: string,
): Promise<ProfileMagicTagRow[]> {
  // Add a per-call header to bypass ApiFetch GET dedupe when a refresh is in-flight.
  const raw = await workspacesApi.tags.list(workspaceId, {
    headers: { "x-pm-refresh": String(Date.now()) },
  });
  return dedupeTagsByKey(unwrapWorkspaceTags(raw));
}

/** One row per key — prefer tag with id, else last in API list */
export function dedupeTagsByKey(tags: ProfileMagicTagRow[]): ProfileMagicTagRow[] {
  const byKey = new Map<string, ProfileMagicTagRow>();
  for (const t of tags) {
    const k = t.key.trim();
    if (!k) continue;
    const prev = byKey.get(k);
    if (!prev) {
      byKey.set(k, t);
      continue;
    }
    if (t.id && !prev.id) {
      byKey.set(k, t);
      continue;
    }
    byKey.set(k, t);
  }
  return Array.from(byKey.values());
}

function tagsWithKey(tags: ProfileMagicTagRow[], key: string): ProfileMagicTagRow[] {
  return tags.filter((t) => t.key.trim() === key);
}

async function deleteWorkspaceTag(
  workspaceId: string,
  tag: ProfileMagicTagRow,
): Promise<void> {
  try {
    if (tag.value) {
      await workspacesApi.tags.deleteByKey(workspaceId, tag.key, tag.value);
    } else {
      await workspacesApi.tags.deleteByKey(workspaceId, tag.key);
    }
  } catch {
    // ignore
  }
}

async function removeTagsForKeys(
  workspaceId: string,
  keys: readonly string[],
  existing: ProfileMagicTagRow[],
): Promise<void> {
  for (const key of keys) {
    for (const row of tagsWithKey(existing, key)) {
      await deleteWorkspaceTag(workspaceId, row);
    }
  }
}

async function upsertOneTag(
  workspaceId: string,
  key: string,
  value: string,
  current: ProfileMagicTagRow[],
): Promise<void> {
  const k = key.trim();
  const v = value.trim();
  if (!k) return;

  const rows = tagsWithKey(current, k);

  // Empty value means "clear this key" — delete each row (API needs key+value).
  if (!v) {
    for (const row of rows) {
      await deleteWorkspaceTag(workspaceId, row);
    }
    return;
  }

  if (rows.length > 0) {
    for (const row of rows) {
      await deleteWorkspaceTag(workspaceId, row);
    }
  }

  try {
    await workspacesApi.tags.create(workspaceId, { key: k, value: v });
  } catch {
    // ignore duplicate races
  }
}

export async function upsertProfileMagicTags(
  workspaceId: string,
  patch: ProfileMagicTagRow[],
  existing?: ProfileMagicTagRow[],
): Promise<ProfileMagicTagRow[]> {
  const current = dedupeTagsByKey(
    existing ?? (await loadProfileMagicTags(workspaceId)),
  );

  for (const { key, value } of patch) {
    await upsertOneTag(workspaceId, key, value, current);
  }

  return loadProfileMagicTags(workspaceId);
}

export function readProfileMagicField(
  tags: ProfileMagicTagRow[],
  key: string,
): string {
  return readCanonicalTagValue(tags, key);
}

export function parseReceivingAccountNames(
  tags: ProfileMagicTagRow[],
): string[] {
  const raw = readProfileMagicField(
    tags,
    PROFILE_MAGIC_TAG_KEYS.businessAccountNames,
  );
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.map(String).map((s) => s.trim()).filter(Boolean);
      }
    } catch {
      // ignore
    }
  }
  const single = readProfileMagicField(
    tags,
    PROFILE_MAGIC_TAG_KEYS.businessAccountName,
  );
  return single ? [single] : [];
}

export function profileMagicReceivingDraft(tags: ProfileMagicTagRow[]) {
  const accountNames = parseReceivingAccountNames(tags);
  const accountNameTh = accountNames[0] ?? "";
  const accountNameEn = accountNames[1] ?? "";
  return {
    bank: readProfileMagicField(tags, PROFILE_MAGIC_TAG_KEYS.businessBank),
    accountNames,
    accountName: accountNameTh,
    accountNameTh,
    accountNameEn,
    showDualNames: accountNames.length > 1,
    accountNumber: readProfileMagicField(
      tags,
      PROFILE_MAGIC_TAG_KEYS.businessAccountNumber,
    ),
  };
}

export function buildReceivingAccountTags(input: {
  accountNameTh: string;
  accountNameEn: string;
  bank?: string;
  /** From slip extract only — not one of the 3 checklist fields */
  accountNumber?: string;
}): ProfileMagicTagRow[] {
  const th = input.accountNameTh.trim();
  const en = input.accountNameEn.trim();
  return [
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessAccountName,
      value: th || en,
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessAccountNames,
      value: th && en ? JSON.stringify([th, en]) : "",
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessBank,
      value: input.bank?.trim() ?? "",
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessAccountNumber,
      value: input.accountNumber?.trim() ?? "",
    },
  ];
}

export function profileMagicShopDocDraft(tags: ProfileMagicTagRow[]) {
  return {
    shopDisplayName: readProfileMagicField(
      tags,
      PROFILE_MAGIC_TAG_KEYS.businessDisplayName,
    ),
    shopLegalName: readProfileMagicField(
      tags,
      PROFILE_MAGIC_TAG_KEYS.businessLegalName,
    ),
    taxId: readProfileMagicField(tags, PROFILE_MAGIC_TAG_KEYS.businessTaxId),
    address: readProfileMagicField(tags, PROFILE_MAGIC_TAG_KEYS.businessAddress),
    branch: readProfileMagicField(tags, PROFILE_MAGIC_TAG_KEYS.businessBranch),
    documentType: readProfileMagicField(
      tags,
      PROFILE_MAGIC_TAG_KEYS.businessDocumentType,
    ),
  };
}

export function parseJsonLinesToArray(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

export async function saveOutstandingSamples(
  workspaceId: string,
  lines: string[],
  existing?: ProfileMagicTagRow[],
) {
  const value = JSON.stringify(lines);
  return upsertProfileMagicTags(
    workspaceId,
    [{ key: PROFILE_MAGIC_TAG_KEYS.businessOutstandingSamples, value }],
    existing,
  );
}

export async function saveRegularCustomers(
  workspaceId: string,
  entries: RegularCustomerAliasEntry[],
  existing?: ProfileMagicTagRow[],
) {
  const current = dedupeTagsByKey(
    existing ?? (await loadProfileMagicTags(workspaceId)),
  );

  const legacyKeys =
    PROFILE_MAGIC_TAG_LEGACY_ALIASES[PROFILE_MAGIC_TAG_KEYS.customerAliases] ??
    [];
  await removeTagsForKeys(workspaceId, legacyKeys, current);

  const value = JSON.stringify(
    entries
      .filter((e) => e.name.trim())
      .map((e) => ({
        name: e.name.trim(),
        ...(e.alias?.trim() ? { alias: e.alias.trim() } : {}),
        ...(e.tax_id?.replace(/\D/g, "")
          ? { tax_id: e.tax_id.replace(/\D/g, "") }
          : {}),
      })),
  );

  return upsertProfileMagicTags(
    workspaceId,
    [{ key: PROFILE_MAGIC_TAG_KEYS.customerAliases, value }],
    current,
  );
}

export async function saveShopDocumentWithPartner(
  workspaceId: string,
  shop: {
    shopDisplayName: string;
    shopLegalName: string;
    taxId: string;
    address: string;
    branch: string;
    documentType: string;
  },
  partner: RegularCustomerAliasEntry | null,
  existing?: ProfileMagicTagRow[],
) {
  const patch = buildShopDocumentTagPatch(shop);
  let next = await upsertProfileMagicTags(workspaceId, patch, existing);
  if (partner?.name.trim()) {
    const customers = parseRegularCustomerAliases(next);
    const merged = mergeRegularCustomerEntry(customers, partner);
    next = await saveRegularCustomers(workspaceId, merged, next);
  }
  return next;
}

export async function saveLegacySheetRows(
  workspaceId: string,
  rows: Array<Record<string, string>>,
  existing?: ProfileMagicTagRow[],
) {
  const value = JSON.stringify(rows);
  return upsertProfileMagicTags(
    workspaceId,
    [{ key: PROFILE_MAGIC_TAG_KEYS.businessLegacySheetRows, value }],
    existing,
  );
}
